/**
 * Account state sync — keeps history/settings/theme in sync across browsers of
 * one linked Telegram account. Offline-first: without a linked account (or on
 * any network failure) the site behaves exactly as before.
 *
 * Design (docs/plan/03-telegram-login/, Decision Log):
 *   - one endpoint POST /api/tg/sync {state, base_rev} -> {state, rev};
 *     the SERVER merges (union-by-id + tombstones + block-LWW) and returns the
 *     canonical state, which fully replaces local storage;
 *   - pull-on-load; push debounced 8s after any 'slovary-state-mutated' event
 *     (dispatched by history/settings/theme writers) + flush on pagehide;
 *   - LIVE pull for already-open tabs: cheap rev probe (/api/tg/sync-status)
 *     on tab focus/visibility + every 90s while visible; the full sync runs
 *     only when the server rev actually moved (another browser pushed);
 *   - tombstones & clear watermark live in localStorage 'slovary-sync-meta'.
 */
(function () {
  'use strict';

  var SYNC_META_KEY = 'slovary-sync-meta';
  var PUSH_DEBOUNCE_MS = 8000;
  var PUSH_MAX_WAIT_MS = 30000; // starvation guard: continuous mutations still push every 30s
  var LIVE_PULL_INTERVAL_MS = 90000;
  var MAX_TOMBSTONES = 500;

  var pushTimer = null;
  var pushFirstScheduledAt = 0;
  var livePullTimer = null;
  var syncInFlight = false;
  var pendingAfterFlight = false;

  // ---- meta (tombstones, clear watermark, LWW stamps, last rev) ----

  function readMeta() {
    try {
      var m = JSON.parse(localStorage.getItem(SYNC_META_KEY) || '{}');
      return {
        deleted: Array.isArray(m.deleted) ? m.deleted : [],
        cleared_before: m.cleared_before || 0,
        settings_updated_at: m.settings_updated_at || 0,
        theme_updated_at: m.theme_updated_at || 0,
        rev: m.rev || 0
      };
    } catch (e) {
      return { deleted: [], cleared_before: 0, settings_updated_at: 0, theme_updated_at: 0, rev: 0 };
    }
  }

  function writeMeta(meta) {
    try {
      if (meta.deleted.length > MAX_TOMBSTONES) {
        meta.deleted = meta.deleted.slice(-MAX_TOMBSTONES);
      }
      localStorage.setItem(SYNC_META_KEY, JSON.stringify(meta));
    } catch (e) { /* quota — sync degrades, site keeps working */ }
  }

  // ---- envelope assembly from the existing storage keys (read-only) ----

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[-]/g, '\\-') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  function collectState() {
    var meta = readMeta();
    var history = [];
    try {
      history = JSON.parse(localStorage.getItem('slovary-search-history') || '[]');
      if (!Array.isArray(history)) history = [];
    } catch (e) { history = []; }
    return {
      v: 1,
      history: history,
      deleted: meta.deleted,
      cleared_before: meta.cleared_before,
      settings: {
        hidden_langs: getCookie('slovary-hidden-langs'),
        hidden_dicts: getCookie('slovary-hidden-dicts'),
        click_search: getCookie('slovary-click-search'),
        history_disabled: localStorage.getItem('slovary-search-history-disabled') || ''
      },
      settings_updated_at: meta.settings_updated_at,
      theme: {
        theme: localStorage.getItem('slovary-theme') || getCookie('slovary_theme') || '',
        color_profile: localStorage.getItem('slovary-color-profile') || getCookie('slovary_color_profile') || ''
      },
      theme_updated_at: meta.theme_updated_at
    };
  }

  // ---- applying the canonical server state locally ----

  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + (365 * 24 * 3600);
  }

  /**
   * Client-side mirror of the server merge (state_merge.php is the SoT of the
   * rules): union-by-id, larger timestamp wins, minus tombstones (union of
   * both sides), minus records older than max(cleared_before); lock flag is
   * monotonic on ts-tie; orphans re-rooted.
   *
   * Needed because the envelope is collected at REQUEST START and applied at
   * RESPONSE TIME: anything the user did in between (a landing via ?search=
   * creates a history record while the initial pull is still in flight, a
   * delete adds a tombstone, ...) is not in the server reply. Blind replace
   * wiped such records — CURRENT_HISTORY_ID became a ghost and the next
   * cross-link child rendered as an orphan root instead of a chain.
   */
  function mergeServerIntoLocal(state, localHistory, localMeta) {
    var changedVsServer = false;
    var clearedBefore = Math.max(state.cleared_before || 0, localMeta.cleared_before || 0);

    var tombs = {}; // id -> ts, union of server reply + local in-flight deletes
    (state.deleted || []).concat(localMeta.deleted || []).forEach(function (t) {
      if (t && t.id && (!(t.id in tombs) || tombs[t.id] < (t.ts || 0))) tombs[t.id] = t.ts || 0;
    });

    var byId = {};
    (state.history || []).forEach(function (rec) {
      if (!rec || !rec.id || (rec.id in tombs) || (rec.timestamp || 0) < clearedBefore) return;
      byId[rec.id] = rec;
    });
    (localHistory || []).forEach(function (rec) {
      if (!rec || !rec.id || (rec.id in tombs) || (rec.timestamp || 0) < clearedBefore) return;
      var cur = byId[rec.id];
      if (!cur) {
        byId[rec.id] = rec;
        changedVsServer = true;
      } else if ((rec.timestamp || 0) > (cur.timestamp || 0)) {
        byId[rec.id] = rec;
        changedVsServer = true;
      } else if ((rec.timestamp || 0) === (cur.timestamp || 0) && rec.locked && !cur.locked) {
        cur.locked = true;
        changedVsServer = true;
      }
    });

    Object.keys(byId).forEach(function (id) {
      var p = byId[id].parentId;
      if (p && !byId[p]) byId[id].parentId = null;
    });

    var history = Object.keys(byId).map(function (id) { return byId[id]; });
    history.sort(function (x, y) {
      return ((x.timestamp || 0) - (y.timestamp || 0)) || (x.id < y.id ? -1 : x.id > y.id ? 1 : 0);
    });

    var deleted = Object.keys(tombs).map(function (id) { return { id: id, ts: tombs[id] }; });
    deleted.sort(function (a, b) { return b.ts - a.ts; });
    if (deleted.length > MAX_TOMBSTONES) deleted = deleted.slice(0, MAX_TOMBSTONES);

    return { history: history, deleted: deleted, cleared_before: clearedBefore, changedVsServer: changedVsServer };
  }

  function applyState(state, rev, mode) {
    if (!state || !Array.isArray(state.history)) return;
    try {
      var meta = readMeta();
      var localDiverged = false;
      var replaceLocal = mode === 'replace_local';

      if (replaceLocal) {
        localStorage.setItem('slovary-search-history', JSON.stringify(state.history));
        meta.deleted = state.deleted || [];
        meta.cleared_before = state.cleared_before || 0;
      } else {
        var localHistory = [];
        try {
          localHistory = JSON.parse(localStorage.getItem('slovary-search-history') || '[]');
          if (!Array.isArray(localHistory)) localHistory = [];
        } catch (e) { localHistory = []; }
        var merged = mergeServerIntoLocal(state, localHistory, meta);
        localStorage.setItem('slovary-search-history', JSON.stringify(merged.history));
        meta.deleted = merged.deleted;
        meta.cleared_before = merged.cleared_before;
        localDiverged = merged.changedVsServer;
      }

      // Settings / theme: whole-block LWW (mirror of the server rule). Apply the
      // server block only when it is not older than local in-flight changes.
      if (replaceLocal || (state.settings_updated_at || 0) >= meta.settings_updated_at) {
        var s = state.settings || {};
        if (s.hidden_langs !== undefined) setCookie('slovary-hidden-langs', s.hidden_langs);
        if (s.hidden_dicts !== undefined) setCookie('slovary-hidden-dicts', s.hidden_dicts);
        if (s.click_search !== undefined) setCookie('slovary-click-search', s.click_search);
        if (s.history_disabled === 'true') {
          localStorage.setItem('slovary-search-history-disabled', 'true');
        }
        meta.settings_updated_at = state.settings_updated_at || 0;
      } else {
        localDiverged = true;
      }

      if (replaceLocal || (state.theme_updated_at || 0) >= meta.theme_updated_at) {
        var t = state.theme || {};
        if (t.theme) {
          localStorage.setItem('slovary-theme', t.theme);
          setCookie('slovary_theme', t.theme);
        }
        if (t.color_profile) {
          localStorage.setItem('slovary-color-profile', t.color_profile);
          setCookie('slovary_color_profile', t.color_profile);
        }
        meta.theme_updated_at = state.theme_updated_at || 0;
      } else {
        localDiverged = true;
      }

      meta.rev = rev || 0;
      writeMeta(meta);

      // Re-render surfaces that read from localStorage lazily.
      if (typeof window.renderHistory === 'function') {
        try { window.renderHistory(false); } catch (e) { /* dropdown may be closed */ }
      }
      document.dispatchEvent(new CustomEvent('slovary-state-synced', { detail: { rev: rev } }));

      // Local contributed something the server does not know yet — converge soon.
      if (localDiverged) schedulePush();
    } catch (e) {
      console.warn('[SYNC] failed to apply server state:', e);
    }
  }

  // ---- transport ----

  function isLinked() {
    return !!(window.TG_ACCOUNT && window.TG_ACCOUNT.linked);
  }

  function doSync(mode) {
    if (syncInFlight) { pendingAfterFlight = true; return Promise.resolve(); }
    syncInFlight = true;
    var meta = readMeta();
    return fetch('/api/tg/sync', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: collectState(), base_rev: meta.rev, mode: mode || 'merge' })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && data.success && data.state) {
          applyState(data.state, data.rev, mode);
        }
      })
      .catch(function () { /* offline-first: silently keep local */ })
      .then(function () {
        syncInFlight = false;
        if (pendingAfterFlight) {
          pendingAfterFlight = false;
          schedulePush();
        }
      });
  }

  function schedulePush() {
    if (!isLinked()) return;
    var now = Date.now();
    if (!pushTimer) pushFirstScheduledAt = now;
    if (pushTimer) clearTimeout(pushTimer);
    // Trailing debounce, but never starve: with continuous mutations the push
    // still fires once the oldest pending change is PUSH_MAX_WAIT_MS old.
    var wait = Math.min(PUSH_DEBOUNCE_MS, Math.max(0, pushFirstScheduledAt + PUSH_MAX_WAIT_MS - now));
    pushTimer = setTimeout(function () {
      pushTimer = null;
      doSync('merge');
    }, wait);
  }

  function flushNow() {
    if (!isLinked()) return;
    if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
    // keepalive-friendly best effort on pagehide
    try {
      var meta = readMeta();
      var payload = JSON.stringify({ state: collectState(), base_rev: meta.rev, mode: 'merge' });
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/tg/sync', new Blob([payload], { type: 'application/json' }));
        return;
      }
    } catch (e) { /* fall through */ }
    doSync('merge');
  }

  // ---- live pull (already-open tab learns about pushes from elsewhere) ----

  function checkAndPull() {
    if (!isLinked() || syncInFlight) return Promise.resolve();
    return fetch('/api/tg/sync-status', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && data.success && typeof data.rev === 'number' && data.rev !== readMeta().rev) {
          return doSync('merge');
        }
      })
      .catch(function () { /* offline: try again next tick */ });
  }

  function startLivePull() {
    if (livePullTimer) return;
    livePullTimer = setInterval(function () {
      if (document.visibilityState === 'visible') checkAndPull();
    }, LIVE_PULL_INTERVAL_MS);
  }

  // ---- mutation listeners ----

  document.addEventListener('slovary-state-mutated', function (ev) {
    var d = (ev && ev.detail) || {};
    var meta = readMeta();
    var now = Date.now();
    if (d.kind === 'history-delete' && Array.isArray(d.ids)) {
      d.ids.forEach(function (id) {
        meta.deleted.push({ id: id, ts: now });
      });
      writeMeta(meta);
    } else if (d.kind === 'history-clear') {
      meta.cleared_before = now;
      meta.deleted = [];
      writeMeta(meta);
    } else if (d.kind === 'settings') {
      meta.settings_updated_at = now;
      writeMeta(meta);
    } else if (d.kind === 'theme') {
      meta.theme_updated_at = now;
      writeMeta(meta);
    }
    schedulePush();
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      flushNow();
    } else if (document.visibilityState === 'visible') {
      // Coming back to this tab (likely from another browser/window):
      // pick up whatever was pushed elsewhere right away.
      checkAndPull();
    }
  });
  window.addEventListener('focus', function () { checkAndPull(); });
  window.addEventListener('pagehide', flushNow);

  // ---- lifecycle: pull on load once the account state is known ----

  document.addEventListener('tg-account-changed', function (ev) {
    if (ev.detail && ev.detail.linked) {
      doSync('merge'); // initial pull+merge (also covers "just linked" moment)
      startLivePull();
    }
  });

  window.SLOVARY_SYNC = {
    syncNow: function () { return doSync('merge'); },
    checkAndPull: checkAndPull,
    replaceServerWithLocal: function () { return doSync('replace_server'); },
    replaceLocalWithServer: function () {
      // Drop local-only history by asking the server for its canonical state.
      return doSync('replace_local');
    }
  };
})();
